import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FrostedPanel from "../../molecules/FrostedPanel";
import Avatar from "../../atoms/Avatar";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

export default function PublicBandPreviewPanel({
    band,
    onBack,
    isLoading = false,
}) {
    const { t } = useTranslation();
    const imageUrl = typeof band?.iconUrl === "string" && band.iconUrl.trim()
        ? resolveMediaUrl(band.iconUrl)
        : null;
    const initials = band?.name
        ? String(band.name)
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() || "")
            .join("")
        : "•";

    if (isLoading) {
        return (
            <FrostedPanel className="bands-page__panel bands-page__panel--main" depth="lg">
                <div className="public-band-preview public-band-preview--empty">
                    <h2>{t("bandsPage.publicPreview.loading.title")}</h2>
                    <p>{t("bandsPage.publicPreview.loading.subtitle")}</p>
                </div>
            </FrostedPanel>
        );
    }

    if (!band) {
        return (
            <FrostedPanel className="bands-page__panel bands-page__panel--main" depth="lg">
                <div className="public-band-preview public-band-preview--empty">
                    <h2>{t("bandsPage.publicPreview.empty.title")}</h2>
                    <p>{t("bandsPage.publicPreview.empty.subtitle")}</p>
                </div>
            </FrostedPanel>
        );
    }

    return (
        <FrostedPanel className="bands-page__panel bands-page__panel--main" depth="lg">
            <div className="public-band-preview">
                <header className="public-band-preview__header">
                    <Avatar
                        src={imageUrl || undefined}
                        alt={`${band.name || "Band"} image`}
                        className="public-band-preview__avatar"
                        size={80}
                        radius={16}
                        fallback={initials || "•"}
                    />
                    <div>
                        <p className="public-band-preview__eyebrow">{t("bandsPage.publicPreview.eyebrow")}</p>
                        <h1 className="public-band-preview__title">{band.name}</h1>
                        {band.description && <p className="public-band-preview__description">{band.description}</p>}
                    </div>
                    <div className="public-band-preview__actions">
                        {onBack && (
                            <button type="button" className="public-band-preview__close" onClick={onBack}>
                                {t("bandsPage.publicPreview.actions.close")}
                            </button>
                        )}
                    </div>
                </header>
                <section className="public-band-preview__grid">
                    <article className="public-band-preview__stat">
                        <span className="public-band-preview__stat-label">{t("bandsPage.publicPreview.stats.members")}</span>
                        <strong className="public-band-preview__stat-value">{band.activeMemberCount ?? 0}</strong>
                    </article>
                    <article className="public-band-preview__stat">
                        <span className="public-band-preview__stat-label">{t("bandsPage.publicPreview.stats.timezone")}</span>
                        <strong className="public-band-preview__stat-value">{band.timezone || "—"}</strong>
                    </article>
                    <article className="public-band-preview__stat">
                        <span className="public-band-preview__stat-label">{t("bandsPage.publicPreview.stats.visibility")}</span>
                        <strong className="public-band-preview__stat-value">
                            {t(`bandsPage.publicPreview.visibility.${(band.visibility || "PUBLIC").toLowerCase()}`, { defaultValue: band.visibility })}
                        </strong>
                    </article>
                    <article className="public-band-preview__stat">
                        <span className="public-band-preview__stat-label">{t("bandsPage.publicPreview.stats.created")}</span>
                        <strong className="public-band-preview__stat-value">
                            {band.createdAt ? new Date(band.createdAt).toLocaleDateString() : "—"}
                        </strong>
                    </article>
                    <article className="public-band-preview__stat">
                        <span className="public-band-preview__stat-label">{t("bandsPage.publicPreview.stats.updated")}</span>
                        <strong className="public-band-preview__stat-value">
                            {band.updatedAt ? new Date(band.updatedAt).toLocaleDateString() : "—"}
                        </strong>
                    </article>
                </section>
                <footer className="public-band-preview__footer">
                    <p>{t("bandsPage.publicPreview.footer")}</p>
                </footer>
            </div>
        </FrostedPanel>
    );
}

PublicBandPreviewPanel.propTypes = {
    band: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        description: PropTypes.string,
        visibility: PropTypes.string,
        linkedBandObjectId: PropTypes.string,
        timezone: PropTypes.string,
        activeMemberCount: PropTypes.number,
        createdAt: PropTypes.string,
        updatedAt: PropTypes.string,
    }),
    onBack: PropTypes.func,
    isLoading: PropTypes.bool,
};
